import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { mcpService } from '../Services/mcpService';
import useGlobals from './useGlobals';
import { setModal } from '../Store/Actions/modalActions';
import toast from 'react-hot-toast';
import { PaginationListType } from '../Types/PaginationParams';
import { setListReloadType } from '../Store/Actions/environmentActions';

interface McpConfig {
    identifier: string;
    title: string;
    description: string;
    serverUrl: string;
    transport: string;
    authType: string;
    lastSync: string | null;
    lastError: string | null;
    toolsCount: number;
}

const useMcpConfigs = () => {
    const navigate = useNavigate();
    const { i18n, dispatch, windowWidth, getBreakpoint, settings } = useGlobals();

    const handleCreateNew = () => {
        navigate('/admin/mcpconfig/new');
    };

    const handleEdit = (config: McpConfig) => {
        navigate(`/admin/mcpconfig/${config.identifier}`);
    };

    const handleDelete = useCallback(
        async (identifier: string) => {
            try {
                await mcpService.deleteConfig(identifier);
                dispatch(setModal({ state: 'closed' }));
                dispatch(setListReloadType(PaginationListType.McpConfigs));
                toast.success(i18n.translate({ id: 'config.delete.success', sourceName: 'MCP' }));
            } catch (error) {
                console.error('Failed to delete MCP config:', error);
                toast.error(i18n.translate({ id: 'config.delete.error', sourceName: 'MCP' }));
            }
        },
        [dispatch, i18n],
    );

    const handleOpenConfirmDialog = useCallback(
        (identifier: string, title: string) => {
            dispatch(
                setModal({
                    state: 'open',
                    title: i18n.translate({ id: 'config.delete.title', sourceName: 'MCP' }),
                    content: i18n.translate({
                        id: 'config.delete.text',
                        sourceName: 'MCP',
                        params: {
                            title: title,
                        },
                    }),
                    buttonRightText: i18n.translate({ id: 'common.cancel', sourceName: 'MCP' }),
                    buttonRightAction: () => handleDelete(identifier),
                    buttonLeftText: i18n.translate({ id: 'common.delete', sourceName: 'MCP' }),
                    buttonLeftAction: () => dispatch(setModal({ state: 'closed' })),
                    modalSize: 'medium',
                    closeOnAction: true,
                    showCloseButton: true,
                }),
            );
        },
        [dispatch, i18n, handleDelete],
    );

    return {
        settings,
        handleOpenConfirmDialog,
        i18n,
        windowWidth,
        getBreakpoint,
        handleEdit,
        handleCreateNew,
    };
};

export default useMcpConfigs;
